import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaFilePdf } from 'react-icons/fa';
import excelIcon from '../../assets/icons/excel.png';
import { Tooltip } from "@mui/material";
import AppDataGrid from '../../components/common/AppDataGrid';

const StudentDiscountReport = () => {
    const userData = sessionStorage.getItem('user');
    const userObj = JSON.parse(userData);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [showTable, setShowTable] = useState(false);
    const [sortModel, setSortModel] = useState([]);
    const [form, setForm] = useState({
        class_id: '',
        section_id: '',
        admission_number: ''
    });

    useEffect(() => {
        fetchClasses();
    }, []);

    useEffect(() => {
        if (form.class_id) fetchSections(form.class_id);
    }, [form.class_id]);

    const fetchClasses = async () => {
        try {
            const res = await axios.post(baseUrl + '/classes/', {
                action: 'READ',
                school_id: userObj.school_id
            });
            setClasses(res.data || []);
        } catch (err) {
            console.error('Error fetching classes:', err);
        }
    };

    const fetchSections = async (class_id) => {
        try {
            const res = await axios.post(baseUrl + '/Sections/', {
                action: 'DROPDOWNREAD',
                class_id,
                school_id: userObj.school_id,
                academic_year_id: userObj.academic_year_id,
            });
            setSections(res.data || []);
        } catch (err) {
            console.error('Error fetching sections:', err);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm(prev => ({ ...prev, [id]: value ?? '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${baseUrl}/getstudentdiscountreport/`, {
                school_id: userObj.school_id,
                academic_year_id: userObj.academic_year_id,
                class_id: form.class_id || null,
                section_id: form.section_id || null,
                admission_number: form.admission_number || null
            });

            const data = res.data || [];
            setShowTable(true);

            if (data.length === 0) {
                setReportData([{ id: 'no-records', student_name: 'No Records Found' }]);
                return;
            }

            setReportData(data);
        } catch (err) {
            toast.error("Something went wrong!");
            console.error("Student Discount Report Error:", err);
        }
    };


    const columns = [
        {
            field: "admission_number",
            headerName: "Admission Number",
            flex: 1,
            minWidth: 140,
            headerAlign: "center",
            align: "center",
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ""}>
                    <span>{params.value ?? ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "student_name",
            headerName: "Student Name",
            flex: 1.2,
            minWidth: 160,
            headerAlign: "center",
            align: "center",
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ""}>
                    <span>{params.value ?? ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "class_name",
            headerName: "Class",
            flex: 0.8,
            minWidth: 110,
            headerAlign: "center",
            align: "center",
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ""}>
                    <span>{params.value ?? ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "section_name",
            headerName: "Section",
            flex: 0.8,
            minWidth: 110,
            headerAlign: "center",
            align: "center",
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ""}>
                    <span>{params.value ?? ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "expected_fee",
            headerName: "Total Fee",
            flex: 1,
            minWidth: 130,
            headerAlign: "center",
            align: "center",
            sortable: true,
            valueGetter: (params) => params.row?.expected_fee ?? 0,
            renderCell: (params) => (
                <Tooltip title={String(params.value ?? 0)}>
                    <span>{params.value ?? 0}</span>
                </Tooltip>
            ),
        },
        {
            field: "total_fee",
            headerName: "Assigned Fee",
            flex: 1,
            minWidth: 130,
            headerAlign: "center",
            align: "center",
            sortable: true,
            valueGetter: (params) => params.row?.total_fee ?? 0,
            renderCell: (params) => (
                <Tooltip title={String(params.value ?? 0)}>
                    <span>{params.value ?? 0}</span>
                </Tooltip>
            ),
        },
        {
            field: "discount",
            headerName: "Discount",
            flex: 0.9,
            minWidth: 120,
            headerAlign: "center",
            align: "center",
            sortable: true,
            valueGetter: (params) => params.row?.discount ?? 0,
            renderCell: (params) => (
                <Tooltip title={String(params.value ?? 0)}>
                    <span>{params.value ?? 0}</span>
                </Tooltip>
            ),
        },
        {
            field: "discount_percentage",
            headerName: "Discount Percentage",
            flex: 1.1,
            minWidth: 160,
            headerAlign: "center",
            align: "center",
            sortable: true,
            valueGetter: (params) => params.row?.discount_percentage ?? 0,
            renderCell: (params) => (
                <Tooltip title={String(params.value ?? 0)}>
                    <span>{params.value ?? 0}</span>
                </Tooltip>
            ),
        },
        {
            field: "remarks",
            headerName: "Remarks",
            flex: 1.2,
            minWidth: 160,
            headerAlign: "center",
            align: "center",
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ""}>
                    <span>{params.value ?? ""}</span>
                </Tooltip>
            ),
        },
    ];


    const filteredData = reportData.filter(item =>
        Object.values(item).join(' ').toLowerCase().includes(searchText.toLowerCase())
    );

    const getSortedData = () => {
        let exportData = [...filteredData];
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];
            exportData.sort((a, b) => {
                let valA = a[field] ?? '';
                let valB = b[field] ?? '';
                
                if (typeof valA === 'string' && typeof valB === 'string') {
                    const numA = Number(valA);
                    const numB = Number(valB);
                    if (!isNaN(numA) && !isNaN(numB) && valA !== '' && valB !== '') {
                        valA = numA;
                        valB = numB;
                    } else {
                        valA = valA.toLowerCase();
                        valB = valB.toLowerCase();
                    }
                }
                
                if (valA < valB) return sort === 'asc' ? -1 : 1;
                if (valA > valB) return sort === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return exportData;
    };

    const exportToPDF = () => {
        const dataToExport = getSortedData();
        if (!showTable || dataToExport.length === 0 || dataToExport[0].student_name === 'No Records Found') {
            toast.error("No data available to export.");
            return;
        }

        const columns = ["Admission No", "Student Name", "Class", "Section", "Total Fee", "Assigned Fee", "Discount", "Remarks"];
        const rows = dataToExport.map(row => [
            row.admission_number || '',
            row.student_name || '',
            row.class_name || '',
            row.section_name || '',
            row.expected_fee || 0,
            row.total_fee || 0,
            row.discount || 0,
            row.remarks || '',
        ]);

        const doc = new jsPDF();
        doc.setFontSize(14);
        doc.text("Student Discount Report", 14, 15);

        autoTable(doc, {
            startY: 20,
            head: [columns],
            body: rows,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [41, 128, 185] }
        });

        doc.save("Student_Discount_Report.pdf");
    };

    const exportToExcel = () => {
        const dataToExport = getSortedData();
        if (!showTable || dataToExport.length === 0 || dataToExport[0].student_name === 'No Records Found') {
            toast.error("No data available to export.");
            return;
        }

        const columns = ["Admission No", "Student Name", "Class", "Section", "Total Fee", "Assigned Fee", "Discount", "Remarks"];
        const rows = dataToExport.map(row => ({
            "Admission No": row.admission_number || '',
            "Student Name": row.student_name || '',
            "Class": row.class_name || '',
            "Section": row.section_name || '',
            "Total Fee": row.expected_fee || 0,
            "Assigned Fee": row.total_fee || 0,
            "Discount": row.discount || 0,
            "Remarks": row.remarks || '',
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "StudentDiscountReport");
        XLSX.writeFile(wb, "Student_Discount_Report.xlsx");
    };

    return (




        <div className='pageBody'>
            <ToastContainer />
            <Container fluid>
                <Card>
                    <Card.Body className="hide-scrollbar">
                        <form onSubmit={handleSubmit}>
                            <Row>
                                <Col xs={12}>
                                    <h6 className='commonSectionTitle'>Student Discount Report</h6>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} md={6} lg={3}>
                                    <Form.Group className="commonInput">
                                        <Form.Label>Admission Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            id="admission_number"
                                            value={form.admission_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter Admission Number"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6} lg={3}>
                                    <Form.Group className="commonInput">
                                        <Form.Label>Class</Form.Label>
                                        <Form.Select id='class_id' value={form.class_id} onChange={handleInputChange}>
                                            <option value='' disabled hidden>Select Class</option>
                                            {classes.map(cls => (
                                                <option key={cls.class_id} value={cls.class_id}>{cls.class_name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6} lg={3}>
                                    <Form.Group className="commonInput">
                                        <Form.Label>Section</Form.Label>
                                        <Form.Select id='section_id' value={form.section_id} onChange={handleInputChange}>
                                            <option value='' disabled hidden>Select Section</option>
                                            {sections.map(sec => (
                                                <option key={sec.section_id} value={sec.section_id}>{sec.section_name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <div className="d-flex justify-content-end mt-4">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            className="btn-info clearBtn me-3"
                                            onClick={() => {
                                                setForm({ class_id: '', section_id: '', admission_number: '' });
                                                setReportData([]);
                                                setShowTable(false);
                                            }}
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="btn-success primaryBtn"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </Card.Body>
                </Card>

                
                    <>
                        <Row className='mt-3 mb-2 align-items-center'>
                            <Col md={3}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </Col>
                            <Col className="d-flex justify-content-end">
                                <Button variant="success" onClick={exportToExcel} className="me-2">
                                    <img src={excelIcon} alt="excel" style={{ width: 20, marginRight: 5 }} />
                                </Button>
                                <Button variant="danger" onClick={exportToPDF}>
                                    <FaFilePdf style={{ marginRight: 5 }} />
                                </Button>
                            </Col>
                        </Row>

                        <div className="commonTable mt-2">
                            <AppDataGrid
                                rows={filteredData?.length ? filteredData : []}
                                columns={columns}
                                getRowId={(row) => row.student_id ?? row.admission_number ?? row.id}
                               pageSizeOptions={[10, 20, 50]} 
                                showToolbar={false}
                                pagination
                                density="compact"
                                maxHeight={400}
                                onSortModelChange={(newModel) => setSortModel(newModel)}
                            />

                        </div>
                    </>
              
            </Container>
        </div>

    );
};

export default StudentDiscountReport;
